import { CREAM, INK, ORB, PALETTE } from '@blorbs/common';
import type { PlayerState } from '@blorbs/common';
import type { WorldView } from '../net';
import type { Input } from '../input';
import { Camera } from './camera';
import { drawLava } from './lava';
import { drawIsland } from './island';
import { drawOrb, faceFor } from './orb';
import { drawFireball } from './fireball';
import { Effects } from './effects';
import { FireballTrails } from './trails';
import { circle, inkStroke } from './art';

export class Renderer {
  readonly camera = new Camera();
  readonly effects = new Effects();
  readonly trails = new FireballTrails();
  private readonly ctx: CanvasRenderingContext2D;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.camera.resize(w, h, dpr);
  }

  draw(view: WorldView, myId: string, input: Input, time: number, dt: number): void {
    const { ctx, camera: cam } = this;
    cam.track(view.islandRadius, dt);
    this.effects.update(dt, cam);

    cam.apply(ctx);
    drawLava(ctx, cam, view.islandRadius, time);
    drawIsland(ctx, cam, view.islandRadius, view.phase === 'playing', time);
    this.effects.drawDecals(ctx, cam);

    // Dead orbs sit under living ones; draw in y order within each group for depth.
    const players = [...view.players].sort((a, b) => Number(a.alive) - Number(b.alive) || a.y - b.y);
    for (const p of players) {
      drawOrb(ctx, cam, p, faceFor(p, view.serverNow, view.winnerId === p.id), view.serverNow, p.id === myId);
    }
    // Flame ribbons under the heads; ribbons of dead fireballs dissipate for a beat.
    for (const f of view.fireballs) this.trails.record(f, time);
    this.trails.prune(new Set(view.fireballs.map((f) => f.id)), time);
    this.trails.update(dt);
    this.trails.draw(ctx, cam, time);
    for (const f of view.fireballs) drawFireball(ctx, cam, f, time);
    this.effects.draw(ctx, cam);

    cam.screen(ctx);
    for (const p of view.players) this.drawLabel(p, p.id === myId);
    if (input.joystick) this.drawJoystick(input.joystick.origin, input.joystick.knob);
  }

  private drawLabel(p: PlayerState, isMe: boolean): void {
    const { ctx, camera: cam } = this;
    const s = cam.toScreen(p.x, p.y);
    const top = s.y - ORB.radius * cam.scale - 10;

    // HP bar.
    const w = 50;
    const h = 7;
    const x = s.x - w / 2;
    const y = top - h;
    ctx.fillStyle = CREAM;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 3.5);
    ctx.fill();
    const hp = Math.max(0, p.hp) / ORB.maxHp;
    if (hp > 0) {
      ctx.fillStyle = hp > 0.6 ? PALETTE[5] : hp > 0.3 ? PALETTE[4] : PALETTE[2];
      ctx.beginPath();
      ctx.roundRect(x, y, Math.max(h, w * hp), h, 3.5);
      ctx.fill();
    }
    ctx.strokeStyle = INK;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 3.5);
    ctx.stroke();

    // Name.
    ctx.font = `${isMe ? 800 : 700} 13px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = CREAM;
    ctx.lineWidth = 4;
    ctx.strokeText(p.name, s.x, y - 3);
    ctx.fillStyle = INK;
    ctx.fillText(p.name, s.x, y - 3);
  }

  private drawJoystick(origin: { x: number; y: number }, knob: { x: number; y: number }): void {
    const { ctx } = this;
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = CREAM;
    circle(ctx, origin.x, origin.y, 56);
    ctx.fill();
    inkStroke(ctx, 3, 1);
    ctx.stroke();
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = PALETTE[4];
    circle(ctx, knob.x, knob.y, 26);
    ctx.fill();
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
}
